// HFP v3 shared rating engine — the ONE implementation every surface uses
// (build instruction sections 4.1–4.4; ticket TD-207).
//
// Faithful to the reference engine (documents/hfp-clinical-dashboard/
// test-harness/target_engine.py) for the sensory resolution, DIFFERS and the
// section 6 acceptance fixtures, extended to the full six-state rule of
// section 4.1 (the reference classifier stops at four states; section 4.1 wins
// where the two differ).
//
// Every tunable is read from scoringConfig.json (never inlined); the profile
// derivation keys (profileTag / responseAxis / raisesMasking / conditionalOn)
// are data-borne from the instrument, never inferred from option position.

import instrument from '../schemas/hfpV3/instrument.json';
import scoringConfig from '../schemas/hfpV3/scoringConfig.json';
import { AREA_WORDS, ENV_AREA_WORDS, RATING_TOKENS } from '../constants/hfpV3Tokens';

// ---- rating state keys ------------------------------------------------------
export const RATING = {
  NOT_APPLICABLE: 'notApplicable',
  INSUFFICIENT: 'insufficient',
  PRIORITY: 'priority',
  SUPPORT: 'support',
  STRENGTH: 'strength',
  NONE: 'none',
};

const SENSORY_DOMAIN = 'Sensory';
const ENV_DOMAIN = 'Environmental Factors';
const GPI_DOMAIN = 'Global Participation Indicators';

// Section 2.7: the borrowed-movement item ids are the build constant, mirrored
// from the reference engine (asked once in Motor & Physical, read by Body & Space).
export const BODYSPACE_MOTOR_ITEM_IDS = [
  'hfpV3MotorPhysical:d450',
  'hfpV3MotorPhysical:d4552',
  'hfpV3MotorPhysical:d4553',
];

const severityIndex = (config) => {
  const order = config.sensory_profile.severity_order; // strength<none<support<priority
  const idx = {};
  order.forEach((band, i) => {
    idx[band] = i;
  });
  return idx;
};

// ---- hfpRating: the six-state ordered rule (section 4.1) --------------------
// `inputs` is a list of mean inputs for one area+context. Each input is
// { value: number|null, isItem: boolean } — value null means asked but not
// answered. A derived input (the Body & Space borrowed movement mean) has
// isItem false: it feeds the mean and validity exactly like the reference
// engine's fourth input, but it is an average, not an item, so it never trips
// the item-count rules (the >=2-items-<=-2 Priority rule, the >=2-items
// strength count, or the no-item-at-or-below strength guard).
export function hfpRating(inputs, { isEnv = false, config = scoringConfig } = {}) {
  const nApplicable = inputs.length;
  if (nApplicable === 0) {
    return { rating: RATING.NOT_APPLICABLE, mean: null, nAnswered: 0, nApplicable: 0 };
  }
  const answered = inputs.filter((i) => i.value !== null && i.value !== undefined);
  const nAnswered = answered.length;
  const V = config.validity;
  // P3: the >=2-valid floor relaxes to >=1 when only one input is
  // context-applicable; P12: the denominator is context-applicable inputs only.
  const minValid = nApplicable === 1 ? 1 : V.min_valid_items;
  const valid = nAnswered >= minValid && nAnswered / nApplicable >= V.min_answered_share;
  if (!valid) {
    return { rating: RATING.INSUFFICIENT, mean: null, nAnswered, nApplicable };
  }

  const mean = answered.reduce((a, i) => a + i.value, 0) / nAnswered;
  const itemValues = answered.filter((i) => i.isItem !== false).map((i) => i.value);
  const B = config.bands;
  const countRule = B.priority_count_rule;
  const nSevere = itemValues.filter((v) => v <= countRule.at_or_below).length;
  if (mean <= B.priority_max_mean || nSevere >= countRule.n_items) {
    return { rating: RATING.PRIORITY, mean, nAnswered, nApplicable };
  }
  if (mean <= B.support_max_mean) {
    return { rating: RATING.SUPPORT, mean, nAnswered, nApplicable };
  }
  const st = B.strength;
  const countLine = isEnv ? st.count_at_or_above_env : st.count_at_or_above;
  const strength =
    (mean >= st.min_mean || itemValues.filter((v) => v >= countLine).length >= st.count_n_items) &&
    !itemValues.some((v) => v <= st.no_item_at_or_below);
  return { rating: strength ? RATING.STRENGTH : RATING.NONE, mean, nAnswered, nApplicable };
}

// ---- the engine -------------------------------------------------------------
// An `answers` object is { itemId: optionId }. The engine runs per context
// ('home' | 'school') with respondent filtering (lg_only excluded from school,
// school_only excluded from home). Conditional items (TD-SEEK/TD-RSP) are read
// ONLY when their conditionalOn is satisfied by the same answer set — an answer
// stored against an unfired follow-up is never read.
export function createHfpEngine({ instrument: inst = instrument, config = scoringConfig } = {}) {
  const items = new Map(inst.items.map((it) => [it.itemId, it]));
  const options = inst.options; // itemId -> [option]
  const optionByItem = {};
  Object.keys(options).forEach((itemId) => {
    optionByItem[itemId] = new Map(options[itemId].map((o) => [o.optionId, o]));
  });
  const sensoryBySense = new Map(); // subdomain -> [item] in file order
  inst.items.forEach((it) => {
    if (it.domain === SENSORY_DOMAIN) {
      if (!sensoryBySense.has(it.subdomain)) sensoryBySense.set(it.subdomain, []);
      sensoryBySense.get(it.subdomain).push(it);
    }
  });
  const SEV = severityIndex(config);

  const numScore = (itemId, optionId) => {
    if (optionId === null || optionId === undefined) return null;
    const opt = optionByItem[itemId] && optionByItem[itemId].get(optionId);
    if (!opt) return null;
    return opt.difficultyScore === null || opt.difficultyScore === undefined
      ? null
      : opt.difficultyScore;
  };

  const excludedRespondent = { home: 'school_only', school: 'lg_only' };

  function scoreContext(answers, context) {
    const exclude = excludedRespondent[context];
    const scored = {};
    inst.items.forEach((it) => {
      const excluded = it.respondent === exclude;
      const raw = excluded ? null : answers[it.itemId] ?? null;
      scored[it.itemId] = {
        item: it,
        rawAnswer: raw,
        excluded,
        score: excluded ? null : numScore(it.itemId, raw),
      };
    });
    return scored;
  }

  const condMet = (it, answers) =>
    !it.conditionalOn || answers[it.conditionalOn.itemId] === it.conditionalOn.optionId;

  // How many sensory questions this answer set asks (always-asked + fired
  // conditionals). Typical child = 27; ceiling = 33.
  function sensoryQuestionsAsked(answers) {
    let n = 0;
    sensoryBySense.forEach((its) => {
      its.forEach((it) => {
        if (condMet(it, answers)) n += 1;
      });
    });
    return n;
  }

  // ---- functional / environmental / GPI classification (sections 4.1) ------
  // Returns { [domain]: { subdomains: {sub: {...}}, mean, rating, isEnv, isGpi } }
  function classifyFunctional(scored) {
    const groups = new Map();
    Object.values(scored).forEach((s) => {
      if (s.item.domain === SENSORY_DOMAIN) return; // sensory has its own model
      if (!groups.has(s.item.domain)) groups.set(s.item.domain, new Map());
      const subs = groups.get(s.item.domain);
      if (!subs.has(s.item.subdomain)) subs.set(s.item.subdomain, []);
      subs.get(s.item.subdomain).push(s);
    });
    const out = {};
    groups.forEach((subs, domain) => {
      const isEnv = domain === ENV_DOMAIN;
      const subResults = {};
      const validMeans = [];
      subs.forEach((list, sub) => {
        const applicable = list.filter((s) => !s.excluded);
        const inputs = applicable.map((s) => ({ value: s.score, isItem: true }));
        const r = hfpRating(inputs, { isEnv, config });
        subResults[sub] = {
          ...r,
          nSevereItems: applicable.filter(
            (s) => s.score !== null && s.score <= config.bands.priority_count_rule.at_or_below
          ).length,
        };
        if (r.mean !== null) validMeans.push(r.mean);
      });
      // Domain rating: the same ordered states from the equal-weighted mean of
      // valid sub-domain means. The inputs are averages, not items, so the
      // item-count rules do not exist at this level (isItem: false throughout).
      const domainRating = hfpRating(
        validMeans.map((m) => ({ value: m, isItem: false })),
        { isEnv, config }
      );
      out[domain] = {
        subdomains: subResults,
        mean: domainRating.mean,
        rating: domainRating.rating,
        isEnv,
        isGpi: domain === GPI_DOMAIN,
      };
    });
    return out;
  }

  // ---- sensory resolution (section 4.3) -------------------------------------
  // Per profiled sense: stage 1 scored spine -> mean band; stage 2 threshold ->
  // at most one follow-up -> label; the label supersedes the mean band, most
  // severe wins, never downward. Pain override on Interoception only.
  function sensoryResolve(scored) {
    const P = config.sensory_profile;
    const PA = config.pain_anchor;
    const profile = {};
    sensoryBySense.forEach((its, sense) => {
      const inputs = [];
      let painOverride = false;
      its.forEach((it) => {
        if (!['detect', 'discriminate', 'use'].includes(it.sensoryRole)) return;
        const s = scored[it.itemId];
        inputs.push({ value: s.score, isItem: true });
        if (
          sense === 'Interoception' &&
          it.code === PA.code &&
          s.score !== null &&
          s.score <= PA.fires_at_or_below
        ) {
          painOverride = true;
        }
      });
      if (sense === 'Body & Space') {
        // the borrowed Motor mean enters as ONE fourth input (an average, not
        // an item); answered iff at least one motor item scored
        const motorScores = BODYSPACE_MOTOR_ITEM_IDS.map((id) => scored[id]?.score).filter(
          (v) => v !== null && v !== undefined
        );
        inputs.push({
          value: motorScores.length
            ? motorScores.reduce((a, b) => a + b, 0) / motorScores.length
            : null,
          isItem: false,
        });
      }
      const stage1 = hfpRating(inputs, { config });
      // senses are always applicable to both respondents, so Not applicable
      // cannot occur here; map it defensively to insufficient.
      const meanBand =
        stage1.rating === RATING.NOT_APPLICABLE ? RATING.INSUFFICIENT : stage1.rating;

      // stage 2: the unscored profile layer (six profiled senses only)
      const thr = its.find((it) => it.sensoryRole === 'threshold') || null;
      let thresholdAnswer = null;
      let thresholdTag = null;
      let label = null;
      let suName = null;
      let strategyItem = null;
      let strategyAnswer = null;
      let masking = false;
      if (thr) {
        thresholdAnswer = scored[thr.itemId].rawAnswer;
        if (thresholdAnswer) {
          const opt = optionByItem[thr.itemId].get(thresholdAnswer);
          thresholdTag = (opt && opt.profileTag) || null;
        }
        if (thresholdTag) {
          // exactly one follow-up can fire: the conditional item gated on this
          // threshold answer (unfired follow-up answers are never read)
          const strat = its.find(
            (it) =>
              ['strategy_seek', 'strategy_reaction'].includes(it.sensoryRole) &&
              it.conditionalOn &&
              it.conditionalOn.itemId === thr.itemId &&
              it.conditionalOn.optionId === thresholdAnswer
          );
          if (strat) {
            strategyItem = strat.code;
            strategyAnswer = scored[strat.itemId].rawAnswer;
            if (strategyAnswer) {
              const o = optionByItem[strat.itemId].get(strategyAnswer);
              if (o) {
                label = (o.profileTag && P.tag_label[o.profileTag]) || null;
                suName = label ? P.su_name[label] : null;
                if (o.raisesMasking) masking = true;
              }
            }
          }
        }
      }

      // resolution: pain override, else the label's rating supersedes the mean
      // band most-severe-wins; a label never lowers a band.
      let band;
      let provenance;
      let raisedByProfile;
      if (painOverride) {
        band = RATING.PRIORITY;
        provenance = 'pain';
        raisedByProfile = false;
      } else if (!label) {
        band = meanBand;
        provenance = 'mean';
        raisedByProfile = false;
      } else {
        const labelBand = P.label_rag[label]; // 'support' | 'priority'
        if (meanBand === RATING.INSUFFICIENT) {
          // the profile layer is independent of the scored layer: a label on an
          // Insufficient mean still carries its rating
          band = labelBand;
          provenance = 'profile';
          raisedByProfile = true;
        } else if (SEV[labelBand] > SEV[meanBand]) {
          band = labelBand;
          provenance = 'profile';
          raisedByProfile = true;
        } else {
          band = meanBand;
          provenance = 'mean';
          raisedByProfile = false;
        }
      }

      let chip = AREA_WORDS[band];
      if (painOverride) chip = PA.chip;
      else if (label) chip = `${AREA_WORDS[band]} · ${label}`;

      profile[sense] = {
        mean: stage1.mean !== null ? Math.round(stage1.mean * 10000) / 10000 : null,
        meanBand,
        band,
        bandDisplay: AREA_WORDS[band],
        chip,
        label,
        suName,
        raisedByProfile,
        provenance,
        painOverride,
        masking,
        thresholdAnswer,
        thresholdTag,
        strategyItem,
        strategyAnswer,
        nAnswered: stage1.nAnswered,
        nApplicable: stage1.nApplicable,
        valid: meanBand !== RATING.INSUFFICIENT,
      };
    });
    return profile;
  }

  // The sensory DOMAIN row: the most severe resolved sense band per context,
  // with no domain mean (bands can be raised, so a mean over them would misread).
  function sensoryDomainSummary(senseProfile) {
    const records = Object.values(senseProfile);
    const rated = records.filter((r) => r.band !== RATING.INSUFFICIENT);
    const worst = rated.length
      ? rated.reduce((a, b) => (SEV[a.band] >= SEV[b.band] ? a : b)).band
      : RATING.INSUFFICIENT;
    return {
      band: worst,
      nRaisedByProfile: records.filter((r) => r.raisedByProfile).length,
      nPainOverrides: records.filter((r) => r.painOverride).length,
    };
  }

  // ---- DIFFERS (section 4.4) -------------------------------------------------
  // Functional: |home mean - school mean| >= mean_gap, both contexts validly
  // rated; a domain also flags when any child sub-domain flags (domain means
  // compared over sub-domains valid in BOTH contexts — paired composition).
  // Sensory: the mean-gap arm OR differing profile outcomes (label, else
  // threshold pole, incl. pole-vs-nothing; untagged-middle disagreement never
  // flags). Env & GPI excluded. Descriptive only — never changes a rating.
  function differs(homeScored, schoolScored) {
    const DF = config.differs;
    const hf = classifyFunctional(homeScored);
    const sf = classifyFunctional(schoolScored);
    const result = { functional: {}, domains: {}, sensory: {} };
    Object.keys(hf).forEach((domain) => {
      if (hf[domain].isEnv || hf[domain].isGpi) return;
      const hsubs = hf[domain].subdomains;
      const ssubs = (sf[domain] || {}).subdomains || {};
      let childDiffers = false;
      const pairedHome = [];
      const pairedSchool = [];
      Object.keys(hsubs).forEach((sub) => {
        const h = hsubs[sub];
        const s = ssubs[sub];
        if (!h || !s || h.mean === null || s.mean === null) return;
        if (
          ![RATING.INSUFFICIENT, RATING.NOT_APPLICABLE].includes(h.rating) &&
          ![RATING.INSUFFICIENT, RATING.NOT_APPLICABLE].includes(s.rating)
        ) {
          pairedHome.push(h.mean);
          pairedSchool.push(s.mean);
          const gap = Math.abs(h.mean - s.mean);
          if (gap >= DF.mean_gap) {
            result.functional[`${domain}::${sub}`] = {
              homeMean: h.mean,
              schoolMean: s.mean,
              gap,
              worseAt: s.mean < h.mean ? 'school' : 'home',
            };
            childDiffers = true;
          }
        }
      });
      const hm = pairedHome.length
        ? pairedHome.reduce((a, b) => a + b, 0) / pairedHome.length
        : null;
      const sm = pairedSchool.length
        ? pairedSchool.reduce((a, b) => a + b, 0) / pairedSchool.length
        : null;
      const domainGapFlag = hm !== null && sm !== null && Math.abs(hm - sm) >= DF.mean_gap;
      if (domainGapFlag || childDiffers) {
        result.domains[domain] = {
          via: domainGapFlag ? 'domain_mean' : 'child_subdomain',
          worseAt: domainGapFlag ? (sm < hm ? 'school' : 'home') : null,
        };
      }
    });
    const hp = sensoryResolve(homeScored);
    const sp = sensoryResolve(schoolScored);
    Object.keys(hp).forEach((sense) => {
      const h = hp[sense];
      const s = sp[sense];
      const via = [];
      if (h.valid && s.valid && Math.abs(h.mean - s.mean) >= DF.mean_gap) via.push('mean_gap');
      if (DF.sensory_tag_outcome) {
        const outcome = (p) => p.label || p.thresholdTag;
        if (outcome(h) !== outcome(s)) via.push('profile_outcome');
      }
      if (via.length) {
        const hSev = SEV[h.band] ?? -1;
        const sSev = SEV[s.band] ?? -1;
        result.sensory[sense] = {
          homeBand: h.band,
          schoolBand: s.band,
          homeOutcome: h.label || h.thresholdTag,
          schoolOutcome: s.label || s.thresholdTag,
          via,
          // equally severe bands render the badge without a direction word
          worseAt: sSev > hSev ? 'school' : hSev > sSev ? 'home' : null,
        };
      }
    });
    return result;
  }

  return {
    config,
    instrument: inst,
    items,
    sensoryBySense,
    severity: SEV,
    scoreContext,
    sensoryQuestionsAsked,
    classifyFunctional,
    sensoryResolve,
    sensoryDomainSummary,
    differs,
  };
}

// ---- the full scoring payload (mirrors the server pipeline) ------------------
// Produces the exact shape the backend persists at domains.hfpV3.scoring
// (python-functions/rms_functions/hfp_v3/engine.py: run_hfp_v3_pipeline), so
// client-side consumers, tests and demo surfaces exercise one payload contract.
export function buildHfpV3Scoring(homeAnswers, schoolAnswers, engine = createHfpEngine()) {
  const homeScored = engine.scoreContext(homeAnswers, 'home');
  const schoolScored = engine.scoreContext(schoolAnswers, 'school');
  const contexts = {};
  [
    ['home', homeScored],
    ['school', schoolScored],
  ].forEach(([context, scored]) => {
    const functional = engine.classifyFunctional(scored);
    const senses = engine.sensoryResolve(scored);
    contexts[context] = {
      functional,
      senses,
      sensoryDomain: engine.sensoryDomainSummary(senses),
      completeness: completeness(functional, senses),
    };
  });
  return {
    configVersion: engine.config.version,
    contexts,
    differs: engine.differs(homeScored, schoolScored),
  };
}

// ---- hfpChip: the ONE chip resolver (sections 4.2.3, 5.2) --------------------
// Returns { word, tokens, subText, provenance, masking } for any area record.
// `kind`: 'functional' | 'env' | 'sense' | 'sensoryDomain'.
// The overview renders outcomes only: callers there ignore subText/provenance.
/**
 * The line under a chip: the mean, and the answered counts only where they
 * exist. A domain record is scored from sub-domain means rather than items, so
 * it deliberately carries no nAnswered/nApplicable (see hfpScoreDomains) — and
 * naming them anyway printed "mean -1.4 · undefined of undefined answered" to
 * clinicians on the HFP clinical view.
 */
function meanSubText(record) {
  if (record.mean === null || record.mean === undefined) return null;
  const line = `mean ${formatMean(record.mean)}`;
  return Number.isFinite(record.nAnswered) && Number.isFinite(record.nApplicable)
    ? `${line} · ${record.nAnswered} of ${record.nApplicable} answered`
    : line;
}

export function hfpChip(record, { kind = 'functional' } = {}) {
  if (kind === 'sense') {
    // Eve 2026-07-21: a labelled sense shows the LABEL as the pill text (the
    // tag supersedes); the resolved band carries the colour. Band words render
    // only when no profile label exists. Pain keeps its safety chip.
    const word = record.painOverride
      ? record.chip // 'Priority area · Pain'
      : record.label || AREA_WORDS[record.band];
    return {
      word,
      tokens: RATING_TOKENS[record.band],
      subText: meanSubText(record),
      provenance: record.painOverride
        ? 'PAIN SAFETY OVERRIDE'
        : record.raisedByProfile
          ? 'RAISED BY PROFILE'
          : null,
      masking: !!record.masking,
    };
  }
  if (kind === 'sensoryDomain') {
    return {
      word: AREA_WORDS[record.band],
      tokens: RATING_TOKENS[record.band],
      subText: null,
      provenance: record.nRaisedByProfile
        ? `${record.nRaisedByProfile} SENSE${record.nRaisedByProfile > 1 ? 'S' : ''} RAISED BY PROFILE`
        : null,
      masking: false,
    };
  }
  const words = kind === 'env' ? ENV_AREA_WORDS : AREA_WORDS;
  return {
    word: words[record.rating],
    tokens: RATING_TOKENS[record.rating],
    subText: meanSubText(record),
    provenance: null,
    masking: false,
  };
}

// Means render to 1 decimal place on clinical surfaces (section 5.2).
// Rounds half away from zero so negative means are not displayed more leniently
// than positive ones (JS Math.round alone would show −1.75 as −1.7 but +1.75 as +1.8).
export function formatMean(mean) {
  const rounded = (Math.sign(mean) * Math.round(Math.abs(mean) * 10)) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}`;
}

// Completeness per context (section 5.1): the share of sub-domain-level areas
// (functional and environmental sub-domains and the seven senses) with a valid
// rating; Not applicable is excluded from the denominator.
export function completeness(functionalClassification, senseProfile) {
  let total = 0;
  let valid = 0;
  Object.values(functionalClassification).forEach((dom) => {
    Object.values(dom.subdomains).forEach((sub) => {
      if (sub.rating === RATING.NOT_APPLICABLE) return;
      total += 1;
      if (sub.rating !== RATING.INSUFFICIENT) valid += 1;
    });
  });
  Object.values(senseProfile).forEach((sense) => {
    total += 1;
    if (sense.band !== RATING.INSUFFICIENT) valid += 1;
  });
  return total ? Math.round((valid / total) * 100) : 0;
}

export default createHfpEngine;
